import { useCallback, useMemo } from "react";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Trash2 } from "lucide-react";
import {
  TextInput,
  Select,
  TextInputWithPrefix,
  Button,
} from "../ui";
import { formatCurrency } from "../../_utils/pricing";
import "./_line-items-editor.scss";

let idCounter = 0;
const generateId = () => `line-item-${Date.now()}-${idCounter++}`;

const DISCOUNT_TYPE_OPTIONS = [
  { label: "Flat", value: "Flat" },
  { label: "Percent", value: "Percent" },
];

const BILLING_TYPE_OPTIONS = [
  { label: "One time", value: "One time" },
  { label: "Recurring", value: "Recurring" },
];

const columnHelper = createColumnHelper();

/**
 * Editable table of one-time-fee line items. `items` is the DERIVED,
 * display-ready array (each row already carries a computed `totalPrice` -
 * see usePricingState, which owns the raw state and derives this via
 * useMemo so the total is never a second, syncable copy of the raw fields).
 * `setRawItems` must be the raw useState setter itself (stable identity by
 * construction) - handlers use the functional-updater form
 * (`setRawItems(prev => ...)`) so they can have an EMPTY dependency array
 * and stay stable across renders regardless of the current item count.
 *
 * Rendered via TanStack Table (column defs + flexRender) rather than a
 * hand-mapped `.map()` over a separate LineItemRow component - purely a
 * rendering-mechanism change, same markup/classNames/styling as before.
 * Column defs replace what used to be LineItemRow's per-field JSX.
 */
const LineItemsEditor = ({
  items,
  setRawItems,
  currencySign,
  title = "One-time fees",
  // Invoice-only: a "Type" column letting each row be tagged Recurring or
  // One time. Plans/Products (Subscription-owned catalog entities) were
  // removed from the Invoice form entirely - a manually-created invoice
  // picking its own plans/products independently of any Subscription
  // corrupted the "Invoice.plans/products is a snapshot copied FROM its
  // linked Subscription" invariant the recurring-billing cron and reporting
  // rely on (docs/domains/16-invoice.md §"Invoice.plans[]/products[]").
  // Invoices now bill exclusively through line items, so they need a way to
  // mark which of those are recurring charges vs one-off fees - Proposal/
  // Subscription forms still have their own real Plan/Product pickers
  // alongside this editor and don't need the extra column.
  showBillingType = false,
}) => {
  const handleAdd = useCallback(() => {
    setRawItems((prev) => [
      ...prev,
      {
        id: generateId(),
        label: "",
        quantity: 1,
        value: "",
        discount: "",
        discountType: "Flat",
        discountApplied: true,
        billingType: "One time",
      },
    ]);
  }, [setRawItems]);

  const handleRowChange = useCallback(
    (id, patch) => {
      setRawItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      );
    },
    [setRawItems],
  );

  const handleRemove = useCallback(
    (id) => {
      setRawItems((prev) => prev.filter((item) => item.id !== id));
    },
    [setRawItems],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("label", {
        header: "Item name",
        cell: (info) => (
          <TextInput
            compact
            placeholder="Item name"
            value={info.getValue() ?? ""}
            onChange={(value) =>
              handleRowChange(info.row.original.id, { label: value })
            }
          />
        ),
      }),
      columnHelper.accessor("quantity", {
        header: "Qty",
        cell: (info) => (
          <TextInput
            compact
            placeholder="Qty"
            value={info.getValue() ?? ""}
            onChange={(value) =>
              handleRowChange(info.row.original.id, { quantity: value })
            }
          />
        ),
      }),
      columnHelper.accessor("value", {
        header: "Amount",
        cell: (info) => (
          <TextInputWithPrefix
            compact
            prefix={currencySign}
            placeholder="0.00"
            value={info.getValue() ?? ""}
            onChange={(value) =>
              handleRowChange(info.row.original.id, { value })
            }
          />
        ),
      }),
      ...(showBillingType
        ? [
            columnHelper.accessor("billingType", {
              header: "Billing type",
              cell: (info) => (
                <Select
                  compact
                  options={BILLING_TYPE_OPTIONS}
                  value={info.getValue() ?? "One time"}
                  onChange={(value) =>
                    handleRowChange(info.row.original.id, { billingType: value })
                  }
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor("discountType", {
        header: "Type",
        cell: (info) => (
          <Select
            compact
            options={DISCOUNT_TYPE_OPTIONS}
            value={info.getValue() ?? "Flat"}
            onChange={(value) =>
              handleRowChange(info.row.original.id, { discountType: value })
            }
          />
        ),
      }),
      columnHelper.accessor("discount", {
        header: "Discount",
        cell: (info) => (
          <TextInput
            compact
            placeholder="Discount"
            value={info.getValue() ?? ""}
            onChange={(value) =>
              handleRowChange(info.row.original.id, { discount: value })
            }
          />
        ),
      }),
      columnHelper.accessor("totalPrice", {
        header: "Total",
        meta: { className: "line-items-editor__col-total" },
        cell: (info) => formatCurrency(info.getValue(), currencySign),
      }),
      columnHelper.display({
        id: "actions",
        header: () => null,
        cell: (info) => (
          <Button
            variant="ghost-danger"
            size="md"
            iconOnly
            icon={<Trash2 size={16} />}
            onClick={() => handleRemove(info.row.original.id)}
            aria-label={`Remove ${info.row.original.label || "line item"}`}
          />
        ),
      }),
    ],
    [currencySign, handleRowChange, handleRemove, showBillingType],
  );

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  return (
    <div className="line-items-editor">
      <div className="line-items-editor__header">
        <h4 className="line-items-editor__title">{title}</h4>
        <Button variant="ghost" size="md" onClick={handleAdd}>
          + Add item
        </Button>
      </div>

      {items.length === 0 ? (
        <p className="line-items-editor__empty">No items added yet.</p>
      ) : (
        <div className="line-items-editor__scroll">
          <table className="line-items-editor__rows">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={header.column.columnDef.meta?.className}
                      aria-hidden={header.id === "actions" || undefined}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cell.column.columnDef.meta?.className}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LineItemsEditor;
